/**
 * TelemetryContext
 * 
 * Global context provider for Socket.IO telemetry connection.
 * Ensures a single persistent connection across the entire app.
 */

import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

const TelemetryContext = createContext(null);

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function TelemetryProvider({ children }) {
  const [liveTelemetry, setLiveTelemetry] = useState({});
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const initializingRef = useRef(false);

  // Initialize Socket.IO connection ONCE when provider mounts
  useEffect(() => {
    // Prevent double initialization in React Strict Mode
    if (initializingRef.current) return;
    initializingRef.current = true;

    if (process.env.NODE_ENV === 'development') {
      console.log('🔌 [TelemetryContext] Initializing Socket.IO connection to:', SOCKET_URL);
    }
    
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      withCredentials: true
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ [TelemetryContext] Connected to telemetry stream');
        console.log('Socket ID:', socket.id);
      }
      setConnected(true);
    });

    socket.on('connect_error', (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ [TelemetryContext] Socket.IO connection error:', error.message);
      }
    });

    socket.on('disconnect', (reason) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('❌ [TelemetryContext] Disconnected from telemetry stream. Reason:', reason);
      }
      setConnected(false);
    });

    // Listen for live telemetry broadcasts
    socket.on('telemetry:live', (telemetry) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('📡 [TelemetryContext] Received live telemetry:', telemetry.imei);
      }
      setLiveTelemetry(prev => ({
        ...prev,
        [telemetry.imei]: {
          ...telemetry,
          receivedAt: new Date().toISOString()
        }
      }));
    });

    // Cleanup only when app unmounts (not on every page navigation)
    return () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 [TelemetryContext] Disconnecting Socket.IO (app unmount)');
      }
      socket.disconnect();
      initializingRef.current = false;
    };
  }, []); // Empty deps - only run once!

  /**
   * Subscribe to telemetry for a specific IMEI
   */
  const subscribeTo = useCallback((imei, callback) => {
    if (!socketRef.current) return () => {};

    const handler = (telemetry) => {
      callback(telemetry);
    };

    socketRef.current.on(`telemetry:${imei}`, handler);

    return () => {
      socketRef.current?.off(`telemetry:${imei}`, handler);
    };
  }, []);

  const value = {
    liveTelemetry,
    connected,
    subscribeTo,
    socket: socketRef.current
  };

  return (
    <TelemetryContext.Provider value={value}>
      {children}
    </TelemetryContext.Provider>
  );
}

/**
 * Hook to access telemetry context
 */
export function useTelemetryContext() {
  const context = useContext(TelemetryContext);
  if (!context) {
    throw new Error('useTelemetryContext must be used within TelemetryProvider');
  }
  return context;
}

export default TelemetryContext;

